<script setup>
import { useSourcesStore } from '../../stores/sources.store';
import { useMetricsStore } from '../../stores/metrics.store';
import AppLoader from '../../components/AppLoader.vue';
import _ from 'lodash';
import { computed, ref, onMounted, onBeforeUnmount, watch, nextTick } from 'vue';
import PageBreadcrumb from '../../components/PageBreadcrumb.vue';
import AppFooter from '../../components/AppFooter.vue';
import HighChart from '../../components/HighChart.vue';
import { Search, Clock, AlertCircle, ChevronsLeft, CheckCircle, Video } from 'lucide-vue-next';
import ActionMenu from '../../components/ActionMenu.vue';
import ItemsPerPageSelect from '@/components/filters/ItemsPerPageSelect.vue';
import TimeRangeFilter from '@/components/filters/TimeRangeFilter.vue';
import { useDeleteConfirmation, useConfirmBeforeAction } from '@/composables/useDialog';
import { useRouter, useRoute } from 'vue-router';
import { getPersistedGlobalConfigValue, getTimeRangeLabel, getSnapshotStatusStyling } from '@/utils/utils';
import { TIME_RANGE_LABELS } from '@/utils/constants';
import { useBreadcrumbs } from '@/composables/useBreadcrumbs';
import { useUrlFilters } from '@/composables/useUrlFilters';
import { FILTER_DEFAULTS } from '@/utils/constants';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import FilteredSnapshotDialog from '@/components/dialogs/FilteredSnapshotDialog.vue';
import SnapshotOptionsDialog from '@/components/dialogs/SnapshotOptionsDialog.vue';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow, TableEmpty } from '@/components/ui/table';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import { Pagination, PaginationContent, PaginationItem, PaginationNext, PaginationPrevious } from '@/components/ui/pagination';
import { Card, CardHeader, CardContent } from '@/components/ui/card';
import { TableEmptyState } from '@/components/ui/table-empty-state';
import {
    getStatusColor,
    formatter,
    getMetricFormattingInfo,
    getAllMetricsForCategoryAndLevel,
    userHasAccess,
    adjustControlConditionalValues,
    adjustControlConditionalVisibility,
    formatChartXAxisLabels,
    createDateFilterFromTimeRange,
    convertFilterKeyToTimeRangeValue,
    filterUserConfigurableConfigs
} from '../../utils/utils';
import { MultiSelect } from '@/components/ui/multi-select';
import TreeView from '../../components/TreeView.vue';
import ConnectorTab from '../../components/ConnectorTab.vue';
import ErrorMessage from '../../components/ErrorMessage.vue';
import WebSocketService from '../../services/webSocket.service';
import { buildTreeNodesOutOfExistingConfig } from '../../utils/utils';
import StatCard from '@/components/global/StatCard.vue';
import Tabs from '@/components/global/Tabs.vue';
import SelectDropdown from '@/components/filters/SelectDropdown.vue';
import DynamicField from '../../components/DynamicField.vue';
import { SkeletonLoader, FormSkeleton } from '@/components/ui/skeleton-loader';
import moment from 'moment-timezone';
import { onBeforeRouteLeave } from 'vue-router';
import { useAuthStore } from '@/stores/auth.store';
import { toast } from '@/composables/useToast';

const router = useRouter();
const route = useRoute();
const authStore = useAuthStore();

const props = defineProps(['id']);
const sourcesStore = useSourcesStore();
const metrics = useMetricsStore();

// Breadcrumb management
const { setDynamicLabel, clearDynamicLabel } = useBreadcrumbs();

const { registerFilter, getFilter, setFilter, resetAllFilters } = useUrlFilters({
    defaults: {
        // Pagination
        page: FILTER_DEFAULTS.PAGINATION.DEFAULT_PAGE,
        pageSize: FILTER_DEFAULTS.PAGINATION.DEFAULT_PAGE_SIZE,

        // Date filters
        selectedTopicsDateFilter: { key: FILTER_DEFAULTS.DATE_FILTERS.ALL_TIME },
        selectedTimeseriesDateFilter: { key: FILTER_DEFAULTS.DATE_FILTERS.LAST_24H },

        // Time unit
        selectedTimeseriesTimeUnit: FILTER_DEFAULTS.TIME_UNITS.HOUR,

        // Topic filters
        topicFilter: null,

        // UI state
        tab: FILTER_DEFAULTS.TABS.STATUS,

        // Time range UI state (for better UX)
        selectedTopicsTimeRange: FILTER_DEFAULTS.TIME_RANGE_VALUES['last24h'], // 86400 seconds
        selectedTimeseriesTimeRange: FILTER_DEFAULTS.TIME_RANGE_VALUES['last24h'], // 86400 seconds

        // Metrics selection
        selectedTimeseriesMetrics: [],

        // Checked topics for bulk actions
        checkedTopics: {}
    },

    // Custom serializers for complex objects
    serializers: {
        selectedTopicsDateFilter: (value) => (value && Object.keys(value).length ? JSON.stringify(value) : null),
        selectedTimeseriesDateFilter: (value) => (value && Object.keys(value).length ? JSON.stringify(value) : null),
        topicFilter: (value) => (value ? JSON.stringify(value) : null),
        selectedTimeseriesMetrics: (value) => (value && value.length ? JSON.stringify(value) : null),
        checkedTopics: (value) => (value && Object.keys(value).length ? JSON.stringify(value) : null),
        selectedTopicsTimeRange: (value) => String(value),
        selectedTimeseriesTimeRange: (value) => String(value)
    },

    // Custom deserializers
    deserializers: {
        selectedTopicsDateFilter: (value) => {
            try {
                return value ? JSON.parse(value) : { key: FILTER_DEFAULTS.DATE_FILTERS.ALL_TIME };
            } catch {
                return { key: FILTER_DEFAULTS.DATE_FILTERS.ALL_TIME };
            }
        },
        selectedTimeseriesDateFilter: (value) => {
            try {
                return value ? JSON.parse(value) : { key: FILTER_DEFAULTS.DATE_FILTERS.LAST_24H };
            } catch {
                return { key: FILTER_DEFAULTS.DATE_FILTERS.LAST_24H };
            }
        },
        topicFilter: (value) => {
            try {
                return value ? JSON.parse(value) : null;
            } catch {
                return null;
            }
        },
        selectedTimeseriesMetrics: (value) => {
            try {
                return value ? JSON.parse(value) : [];
            } catch {
                return [];
            }
        },
        checkedTopics: (value) => {
            try {
                return value ? JSON.parse(value) : {};
            } catch {
                return {};
            }
        },
        selectedTopicsTimeRange: (value) => parseInt(value) || FILTER_DEFAULTS.TIME_RANGE_VALUES['last24h'],
        selectedTimeseriesTimeRange: (value) => parseInt(value) || FILTER_DEFAULTS.TIME_RANGE_VALUES['last24h']
    }
});

// Register all filters and get reactive references
const page = registerFilter('page', 0);
const pageSize = registerFilter('pageSize', 10);
const selectedTopicsDateFilter = registerFilter('selectedTopicsDateFilter', { key: FILTER_DEFAULTS.DATE_FILTERS.ALL_TIME });
const selectedTimeseriesDateFilter = registerFilter('selectedTimeseriesDateFilter', { key: FILTER_DEFAULTS.DATE_FILTERS.LAST_24H });
const selectedTimeseriesTimeUnit = registerFilter('selectedTimeseriesTimeUnit', FILTER_DEFAULTS.TIME_UNITS.HOUR);
const topicFilter = registerFilter('topicFilter', null);
const currentTab = registerFilter('tab', FILTER_DEFAULTS.TABS.STATUS);
const selectedTimeseriesMetrics = registerFilter('selectedTimeseriesMetrics', []);
const checkedTopics = registerFilter('checkedTopics', {});
const selectedTopicsTimeRange = registerFilter('selectedTopicsTimeRange', FILTER_DEFAULTS.TIME_RANGE_VALUES['last24h']);
const selectedTimeseriesTimeRange = registerFilter('selectedTimeseriesTimeRange', FILTER_DEFAULTS.TIME_RANGE_VALUES['last24h']);

let source = ref();
let defaultSource = ref({});

let generalLoading = ref(false);
let configurationLoading = ref(false);
let treeLoading = ref(false);
let metricsLoading = ref(false);
let timeseriesLoading = ref(false);
let hidePromptTemporary = ref(false);
let isInitializing = ref(true);

// Filtered snapshot modal state
const showFilteredSnapshotDialog = ref(false);
const showSnapshotOptionsDialog = ref(false);
const selectedTopicsForFiltering = ref([]);

let tabLoading = ref(false);
let actionLoading = ref(false);
let topicActionLoading = ref({});
let topicsLoading = ref(false);
let sourceConfig = ref(null);
let formError = ref('');

let canSave = ref(false);
let canSaveSchemaTree = ref(false);
let schemaTreeIsValid = ref(false);

let treeLevels = ref([]);
let treeNodes = ref([]);

let defaultTreeLevels = ref([]);
let defaultTreeNodes = ref([]);

// Time range variables now managed by useUrlFilters composable above

const timeUnitOptions = [
    { name: 'Month', value: FILTER_DEFAULTS.TIME_UNITS.MONTH },
    { name: 'Day', value: FILTER_DEFAULTS.TIME_UNITS.DAY },
    { name: 'Hour', value: FILTER_DEFAULTS.TIME_UNITS.HOUR },
    { name: 'Minute', value: FILTER_DEFAULTS.TIME_UNITS.MINUTE }
];

let noExistingSchema = ref(false);
const historicalCheckedTopicNames = ref({});

const allTopicsAreChecked = ref(false);

// Metrics selection now managed by selectedTimeseriesMetrics filter above

const selectedMetricKeys = computed({
    get() {
        return selectedTimeseriesMetrics.value.map((metric) => metric.key);
    },
    set(keys) {
        selectedTimeseriesMetrics.value = keys.map((key) => listOfTimeseriesMetricOptions.value.find((metric) => metric.key === key)).filter(Boolean);
    }
});

let plotSpecificMetrics = ref(null);
let specificTopicMetricsToUse = ref(null);

// Responsive layout for lateral description
const hideLateralDescriptionValue = ref(true);

const handleResize = () => {
    hideLateralDescriptionValue.value = window.innerWidth <= 1024;
};

watch(
    [treeLevels, treeNodes],
    (values) => {
        canSaveSchemaTree.value = !_.isEqual(values[0], defaultTreeLevels.value) || !_.isEqual(values[1], defaultTreeNodes.value);
    },
    { deep: true, immediate: true }
);

// Update breadcrumb label when source name becomes available
watch(
    () => source.value?.name,
    (newName) => {
        if (newName) {
            setDynamicLabel('source-details', newName);
        }
    },
    { immediate: true }
);

// Watch for filter changes and automatically refetch topics data
watch(
    [page, pageSize, topicFilter, selectedTopicsDateFilter],
    async () => {
        if (source.value && !isInitializing.value) {
            await fetchTopicsData();
        }
    },
    { deep: true }
);

// Watch for timeseries filter changes and automatically refetch timeseries data
watch(
    [selectedTimeseriesDateFilter, selectedTimeseriesTimeUnit],
    async () => {
        if (source.value && !isInitializing.value) {
            await fetchTimeseriesData();
        }
    },
    { deep: true }
);

// Watch for changes in checked topics to update bulk selection state
watch(
    [checkedTopics, specificTopicMetricsToUse],
    () => {
        const allCheckedIds = Object.keys(checkedTopics.value).filter((x) => checkedTopics.value[x]);
        const currentTopics = topics(specificTopicMetricsToUse.value) || [];
        const allTopicsShownAreChecked = currentTopics.length > 0 && currentTopics.map((x) => x.id).every((x) => allCheckedIds.includes(x));
        allTopicsAreChecked.value = allTopicsShownAreChecked;
    },
    { deep: true }
);

const canSaveButton = computed(() => {
    return canSave.value || (canSaveSchemaTree.value && schemaTreeIsValid.value);
});

// Disable save on Schema tab if no tree nodes are selected
const hasAnySchemaSelection = computed(() => {
    const checkNodes = (nodes) => nodes.some((n) => n.isSelected || (n.children && checkNodes(n.children)));

    return checkNodes(treeNodes.value || []);
});

onBeforeRouteLeave((to, from, next) => {
    if (
        generalLoading.value ||
        configurationLoading.value ||
        treeLoading.value ||
        metricsLoading.value ||
        timeseriesLoading.value ||
        tabLoading.value
    ) {
        return next();
    }

    if (!canSaveButton.value) {
        return next();
    }

    const answer = window.confirm('Are you sure you want to leave? You have unsaved changes.');

    if (answer) {
        return next();
    } else {
        return next(false);
    }
});

const visibleConnectorSchemaConfig = computed(() => {
    const filteredConfigs = filterUserConfigurableConfigs(sourceConfig.value);
    const connectorConfig =
        filteredConfigs?.filter((x) => !x.display_advanced).filter((x) => x.tab === 'schema' && !x.schema_level && !x.schema_name_format) || [];

    return connectorConfig;
});

const visibleSchemaConnectorConfigControls = computed(() => {
    const filteredConfigs = filterUserConfigurableConfigs(sourceConfig.value);

    const adjustedControls = _.chain(adjustControlConditionalValues(visibleConnectorSchemaConfig.value))
        .filter((x) => !x.display_advanced)
        .orderBy('order_of_display', 'asc')
        .map((control) => adjustControlConditionalVisibility(control, filteredConfigs)?.control || null)
        .compact()
        .value();

    let controlsToRecheck = adjustedControls.filter((c) => c.conditions?.length);

    if (controlsToRecheck.length) {
        controlsToRecheck.map((oldControl) => {
            return onFieldChanged({
                value: oldControl.value.current_value || oldControl.value.default,
                valid: oldControl.value.valid || false,
                name: oldControl.name
            });
        });
    }

    return adjustedControls;
});

function getClass(control, index) {
    // Ensure a default value if undefined.
    const spaceLeft = control.space_left || 0;

    // Pick the correct array based on the mode.
    const usedConnectors = visibleSchemaConnectorConfigControls.value;

    const nextControl = usedConnectors[index + 1];
    const nextControlSpaceLeft = nextControl ? nextControl.space_left : undefined;

    // Predefined mapping for padding-left classes.
    // Extend this mapping if you expect more spacing values.
    const paddingLeftMapping = {
        0: 'pl-0',
        1: 'pl-5',
        2: 'pl-10',
        3: 'pl-15',
        4: 'pl-20',
        5: 'pl-25',
        6: 'pl-30'
    };

    const paddingLeftClass = paddingLeftMapping[spaceLeft] || '';

    let marginBottomClass = 'mb-6'; // Default margin for same level.

    if (nextControlSpaceLeft === undefined) {
        // Last control: standard margin.
        marginBottomClass = 'mb-6';
    } else if (nextControlSpaceLeft > spaceLeft) {
        // Next control is a child: smaller margin.
        marginBottomClass = 'mb-2';
    } else if (nextControlSpaceLeft === spaceLeft) {
        // Next control is at the same level: standard margin.
        marginBottomClass = 'mb-6';
    } else if (nextControlSpaceLeft < spaceLeft) {
        // Next control is at a higher level: larger margin.
        marginBottomClass = 'mb-8';
    }

    return `${paddingLeftClass} ${marginBottomClass}`.trim();
}

// Initialize default page size for topics
if (pageSize.value === 0) {
    pageSize.value = 10; // Default topics per page
}

function updateCanSave(newValue) {
    canSave.value = newValue;
}

onBeforeUnmount(() => {
    window.removeEventListener('resize', handleResize);
    removeWebsocketListeners();
    clearDynamicLabel('source-details');
});

onMounted(async () => {
    // Setup responsive handler
    handleResize();
    window.addEventListener('resize', handleResize);

    try {
        generalLoading.value = true;
        treeLoading.value = true;
        configurationLoading.value = true;
        metricsLoading.value = true;

        // Set the initializing flag to prevent watchers from triggering during load
        isInitializing.value = true;

        // Tab is automatically initialized by useUrlFilters composable

        // Set default pageSize if not already set
        if (getFilter('pageSize') === 0) {
            setFilter('pageSize', 10);
        }

        // Wait a tick to ensure filter values are restored from URL
        await nextTick();

        // Initial data loading using current filter state (includes URL-restored values)
        const connectorMetricsPromise = metrics.sources.fetch(props.id, {
            topic_list_from: page.value * pageSize.value,
            topic_list_size: pageSize.value,
            level: 'connector',
            time_type: 'latest'
        });

        // Load timeseries data with current filter state
        const timeseriesPromise = fetchTimeseriesData();

        // Load topics data with current filter state
        const topicMetricsPromise = fetchTopicsData();

        await Promise.all([
            sourcesStore.getById(props.id, route?.query?.customMessage).then(async (data) => {
                source.value = JSON.parse(JSON.stringify(data ?? {}));
                defaultSource.value = _.cloneDeep(source.value);

                if (route?.query?.customMessage && data) {
                    // Remove customMessage from URL after handling
                    const newQuery = { ...route.query };
                    delete newQuery.customMessage;
                    router.replace({ query: newQuery });
                }

                generalLoading.value = false;

                await sourcesStore.fetchConfiguration(source.value.connector).then(() => {
                    sourceConfig.value = sourcesStore.configurations[source.value.connector];
                    treeLoading.value = false;
                    configurationLoading.value = false;

                    if (sourceConfig.value?.some((cfg) => cfg.tab === 'schema')) {
                        constructTreeNodes();
                        defaultTreeLevels.value = _.cloneDeep(treeLevels.value);
                        defaultTreeNodes.value = _.cloneDeep(treeNodes.value);
                    } else {
                        noExistingSchema.value = true;
                    }

                    if (sourceConfig.value?.some((cfg) => cfg.tab === 'smt')) {
                        tabs.value.push({ name: 'Single Message Transforms', key: 'smt', href: '#', current: false });
                    }
                });
            }),
            sourcesStore.fetchConnectors(),
            connectorMetricsPromise.then(() => {
                metricsLoading.value = false;
            }),
            timeseriesPromise.then(() => {
                timeseriesLoading.value = false;
            }),
            topicMetricsPromise.then(() => {
                topicsLoading.value = false;
            })
        ]);
    } finally {
        generalLoading.value = false;
        configurationLoading.value = false;
        metricsLoading.value = false;
        treeLoading.value = false;
        tabLoading.value = false;
        timeseriesLoading.value = false;
        topicsLoading.value = false;
        isInitializing.value = false; // Allow watchers to trigger after initial load
    }

    addWebsocketListeners();
});

const addWebsocketListeners = () => {
    WebSocketService.addListener({
        topic: 'metrics',
        entityType: 'sources',
        entityId: props.id,
        callback: metrics.onMetricsData,
        callbackId: 'metrics.onMetricsData',
        startListFrom: page.value * pageSize.value,
        listSize: pageSize.value
    });
};

const removeWebsocketListeners = () => {
    WebSocketService.removeListener({
        topic: 'metrics',
        entityType: 'sources',
        entityId: props.id,
        callbackId: 'metrics.onMetricsData',
        startListFrom: page.value * pageSize.value,
        listSize: pageSize.value
    });
};

const constructTreeNodes = () => {
    let configProps = source.value.config;
    let sourceConnector = Object.values(sourcesStore.sourceConnectors)
        .flat()
        .find((x) => x.connector == source.value.connector);

    const nodes = buildTreeNodesOutOfExistingConfig(configProps, sourceConfig.value, sourceConnector);

    treeNodes.value = nodes.treeNodes;
    treeLevels.value = nodes.treeLevels;
};

const connectorInfo = computed(() => {
    return Object.values(sourcesStore.sourceConnectors || {})
        .flat()
        .find((c) => c.connector === source.value?.connector);
});

const sourceLatestMetrics = computed(() => {
    return metrics.sources.allLatest[props.id] || {};
});

const sourceTimeseriesConnectorMetrics = computed(() => {
    return metrics.sources.allTimeseries[props.id]?.connector || {};
});

const isDefaultTopicDateFilter = computed(() => {
    return (
        selectedTopicsDateFilter.value.key === FILTER_DEFAULTS.DATE_FILTERS.ALL_TIME ||
        !selectedTopicsDateFilter.value ||
        !selectedTopicsDateFilter.value?.key
    );
});

const isDefaultTimeseriesDateFilter = computed(() => {
    return selectedTimeseriesDateFilter.value?.key === FILTER_DEFAULTS.DATE_FILTERS.LAST_24H || !selectedTimeseriesDateFilter.value?.key;
});

const topics = (useSpecificMetrics) => {
    const compValue = computed(() => {
        // prefer topic-level metrics, fallback to connector-level topics metrics
        const metricsByTopic = useSpecificMetrics?.topics || sourceLatestMetrics.value?.topics || {};

        return (
            Object.keys(metricsByTopic)
                .map((t) => {
                    const topicName = source.value?.topics?.find((x) => t.toLowerCase().includes(x.toLowerCase()) && t.split(x)?.[1] === '');
                    // try snapshotStatus from topic_metrics first
                    const topicData = metricsByTopic[t] || {};
                    let entry =
                        Array.isArray(topicData.snapshotStatus) && topicData.snapshotStatus.length
                            ? topicData.snapshotStatus.find((s) => s.topic_id.split('.').slice(1).join('.') === t) || topicData.snapshotStatus[0]
                            : null;
                    // fallback to connector-level snapshotStatus array
                    if (!entry) {
                        entry =
                            sourceLatestMetrics.value.connector?.snapshotStatus?.find((s) => s.topic_id.split('.').slice(1).join('.') === t) || {};
                    }
                    const status = entry.status || 'not started';
                    return {
                        id: t,
                        name: topicName,
                        snapshotStatus: status,
                        rawStatus: entry.status,
                        snapshotDisplayStatus: entry.status ? _.capitalize(entry.status) : 'Not Started',
                        snapshotStartTimestamp: entry.start_timestamp,
                        snapshotEndTimestamp: entry.end_timestamp,
                        snapshotDurationMs: entry.duration_ms,
                        snapshotRowsScanned: entry.rows_scanned,
                        snapshotRowsTotal: entry.rows_total
                    };
                })
                .filter((x) => x) || []
        );
    });

    return compValue.value;
};

const listOfTimeseriesMetricOptions = computed(() => {
    if (generalLoading.value || timeseriesLoading.value) return [];

    const defaultDateFilter =
        selectedTimeseriesDateFilter.value?.key === FILTER_DEFAULTS.DATE_FILTERS.LAST_24H ||
        !selectedTimeseriesDateFilter.value ||
        !selectedTimeseriesDateFilter.value?.key;
    const metricsToUse = defaultDateFilter ? sourceTimeseriesConnectorMetrics.value : plotSpecificMetrics.value?.connector;

    let availableTimeSeriesMetricKeys = Object.keys(metricsToUse || {});

    availableTimeSeriesMetricKeys = availableTimeSeriesMetricKeys
        .map((key) => {
            const metricInfo = getMetricFormattingInfo(key, connectorInfo.value?.metrics);

            if (!metricInfo) {
                return null;
            }

            return {
                key,
                ...metricInfo
            };
        })
        .filter((x) => x && metricsToUse[x.key]?.length);

    // Don't clear metrics selection on API failure to avoid infinite loops
    // The watcher will handle validation of persisted metrics

    return _.orderBy(availableTimeSeriesMetricKeys, [(item) => item.key !== 'sourceRecordWriteTotal'], ['asc']);
});

watch(
    () => listOfTimeseriesMetricOptions.value,
    (newValue) => {
        const defaultTo = ['StreamingMilliSecondsBehindSource'];

        // Only set default metrics if none are selected AND no persisted metrics from URL
        if (!selectedTimeseriesMetrics.value.length && newValue.length) {
            const defaultMetrics = newValue.filter((x) => defaultTo.includes(x.key));
            selectedTimeseriesMetrics.value = defaultMetrics.length ? defaultMetrics : [newValue[0]];
        }

        // If we have persisted metrics from URL, validate they still exist in available metrics
        else if (selectedTimeseriesMetrics.value.length && newValue.length) {
            const validMetrics = selectedTimeseriesMetrics.value.filter((selected) => newValue.find((available) => available.key === selected.key));
            // Update to full metric objects if we only have keys persisted
            selectedTimeseriesMetrics.value = validMetrics.map(
                (selected) => newValue.find((available) => available.key === selected.key) || selected
            );
        }
    }
);

async function fetchTopicsData() {
    try {
        topicsLoading.value = true;
        removeWebsocketListeners();

        const dateFilterToUse = isDefaultTopicDateFilter.value
            ? {}
            : {
                  start: selectedTopicsDateFilter.value?.value?.start,
                  end: selectedTopicsDateFilter.value?.value?.end,
                  key: selectedTopicsDateFilter.value?.key
              };

        const filterParams = topicFilter.value
            ? {
                  ...topicFilter.value.filterOption,
                  filterType: topicFilter.value.filter.name
              }
            : {};

        const params = {
            topic_list_from: page.value * pageSize.value,
            topic_list_size: pageSize.value,
            time_type: !isDefaultTopicDateFilter.value ? 'timesummary' : undefined,
            ...dateFilterToUse,
            ...filterParams,
            level: 'topic'
        };

        specificTopicMetricsToUse.value = await metrics.sources.fetch(props.id, params);

        if (isDefaultTopicDateFilter.value) {
            specificTopicMetricsToUse.value = null;
        }

        addWebsocketListeners();
    } finally {
        topicsLoading.value = false;
    }
}

async function fetchTimeseriesData() {
    try {
        timeseriesLoading.value = true;

        const isDefaultFilter =
            selectedTimeseriesDateFilter.value?.key === FILTER_DEFAULTS.DATE_FILTERS.LAST_24H || !selectedTimeseriesDateFilter.value?.key;

        const params = isDefaultFilter
            ? { timeUnit: selectedTimeseriesTimeUnit.value }
            : {
                  start: selectedTimeseriesDateFilter.value?.value?.start,
                  end: selectedTimeseriesDateFilter.value?.value?.end,
                  key: selectedTimeseriesDateFilter.value?.key,
                  timeUnit: selectedTimeseriesTimeUnit.value
              };

        const timeseriesData = await metrics.sources.fetchTimeseriesData(props.id, params);
        plotSpecificMetrics.value = isDefaultFilter ? null : timeseriesData;
    } finally {
        timeseriesLoading.value = false;
    }
}

const availableMetrics = computed(() => {
    if (isDefaultTopicDateFilter.value && (!connectorInfo.value?.metrics || _.isEmpty(connectorInfo.value?.metrics))) return null;

    //const streamingMetrics = getAllMetricsForCategoryAndLevel(connectorInfo.value?.metrics, 'streaming', null, 'sources');
    const topicMetrics = getAllMetricsForCategoryAndLevel(connectorInfo.value?.metrics, null, 'topic', 'sources');

    return {
        // streaming: streamingMetrics,
        topic: topicMetrics
    };
});

async function loadSourceUponAction() {
    tabLoading.value = true;
    try {
        metricsLoading.value = true;

        await Promise.all([sourcesStore.fetchConnectors(), metrics.sources.fetch(props.id, { level: 'inline', time_type: 'latest' })]);

        const sourceVal = await sourcesStore.getById(props.id, null, true);

        source.value = JSON.parse(JSON.stringify(sourceVal ?? {}));
        defaultSource.value = _.cloneDeep(source.value);

        constructTreeNodes();
    } catch (e) {
        console.error('Error in loadSourceUponAction:', e);
    } finally {
        tabLoading.value = false;
        metricsLoading.value = false;
    }
}

async function onStop(id) {
    try {
        actionLoading.value = true;
        await sourcesStore.stop(id);
        await loadSourceUponAction();
    } finally {
        actionLoading.value = false;
    }
}

async function refreshTimeseries() {
    // Simply call the main fetch function and scroll to position
    await fetchTimeseriesData();
    await scrollToCurrentPosition();
}

async function onResume(id) {
    try {
        actionLoading.value = true;
        await sourcesStore.resume(id);
        await loadSourceUponAction();
    } finally {
        actionLoading.value = false;
    }
}

async function onRestart(id) {
    try {
        actionLoading.value = true;
        await sourcesStore.restart(id);
        await loadSourceUponAction();
    } finally {
        actionLoading.value = false;
    }
}

async function onReset(source) {
    const message =
        `Are you sure you want to reset '<strong>${source.name}</strong>'?<br><br>` +
        `<ul>` +
        `<li>The connector will stop and restart from the beginning</li>` +
        `<li>All previously processed data will be reprocessed</li>` +
        `<li>The connector most likely will require a new snapshot</li>` +
        `<li>This may cause duplicate events in downstream systems</li>` +
        `<li><strong>This action cannot be undone</strong></li>` +
        `</ul>`;

    try {
        await useConfirmBeforeAction(
            'reset',
            async () => {
                actionLoading.value = true;
                await sourcesStore.reset(source.id);
                await loadSourceUponAction();
                actionLoading.value = false;
            },
            {
                message,
                expectedInput: 'reset'
            }
        );
    } catch (err) {
        console.log('Reset cancelled or failed:', err);
        actionLoading.value = false;
    }
}

function onClone(source) {
    router.push({
        name: 'new-source',
        params: { connector: source.connector },
        query: { cloneConfig: JSON.stringify(source.config) }
    });
}

async function onDelete(entity, name, id, index) {
    tabLoading.value = true;
    hidePromptTemporary.value = true;

    try {
        await useDeleteConfirmation({
            entity,
            name,
            title: `Delete ${entity}`,
            message: `Are you sure you want to delete the ${entity} <strong>"${name}"</strong>? This action cannot be undone and you will be redirected to the connectors page.`,
            onConfirm: async () => {
                await sourcesStore.delete(id);
                sourcesStore.sources = undefined;
                router.push({ name: 'sources-list' });
            }
        });
    } catch (error) {
        // User cancelled or error occurred
        if (error.message) {
            console.error('Delete failed:', error);
        }
    } finally {
        tabLoading.value = false;
        hidePromptTemporary.value = false;
    }
}

function onTopicsPageChange(newPage) {
    page.value = newPage;
    // Data refetching is handled automatically by watchers
}

function onItemsPerPageChange(newPageSize) {
    pageSize.value = newPageSize;
    page.value = 0;
    // Data refetching is handled automatically by watchers
}

function getVisibleTopicsPages() {
    const totalPages = Math.ceil((source.value?.topics?.length ?? 0) / pageSize.value);
    const currentPage = page.value + 1;
    const siblingCount = 1;
    const pages = [];

    // Always show first page
    pages.push(1);

    // Calculate start and end of visible range
    const startPage = Math.max(2, currentPage - siblingCount);
    const endPage = Math.min(totalPages - 1, currentPage + siblingCount);

    // Add ellipsis after first page if needed
    if (startPage > 2) {
        pages.push('...');
    }

    // Add pages in visible range
    for (let i = startPage; i <= endPage; i++) {
        if (i !== 1 && i !== totalPages) {
            pages.push(i);
        }
    }

    // Add ellipsis before last page if needed
    if (endPage < totalPages - 1) {
        pages.push('...');
    }

    // Always show last page (if different from first)
    if (totalPages > 1) {
        pages.push(totalPages);
    }

    return pages;
}

function onTopicsFilterToggle({ filter, filterOption }) {
    if (filter.name === 'count') {
        pageSize.value = filterOption.value;
    }

    page.value = 0;
    const topicFilterData = { filter: { name: filter.name }, filterOption };
    topicFilter.value = topicFilterData;
    // Data refetching is handled automatically by watchers
}

// Filtered snapshot feature flags
const filteredSnapshotDisabled = computed(() => {
    if (!source.value) return false;
    const disabled = getPersistedGlobalConfigValue(source.value, 'filtered.snapshot.disabled');
    return disabled === true;
});

const filteredSnapshotLanguage = computed(() => {
    if (!source.value) return 'SQL';
    const language = getPersistedGlobalConfigValue(source.value, 'filtered.snapshot.language');
    return language || 'SQL';
});

const canConnectorCancelSnapshot = computed(() => {
    // Check if snapshot is disabled via global config
    if (!source.value) return false; // Wait for source to be loaded
    const snapshotDisabled = getPersistedGlobalConfigValue(source.value, 'incremental.snapshot.disabled');
    if (snapshotDisabled === true) return false;

    return true;
    //if (NO_SNAPSHOT_CONNECTORS.includes(source.value.connector)) return false;
    // const cm = sourceLatestMetrics.value.connector || {};

    // if (cm.snapshotState === 'running' || cm.snapshotState === 'pending') return true;

    // if (cm.connector_status?.toLowerCase() === 'paused') return false;

    // return false;
});

const canConnectorSnapshot = computed(() => {
    // Check if snapshot is disabled via global config
    if (!source.value) return false; // Wait for source to be loaded
    const snapshotDisabled = getPersistedGlobalConfigValue(source.value, 'incremental.snapshot.disabled');
    if (snapshotDisabled === true) return false;

    return true;
    //  if (NO_SNAPSHOT_CONNECTORS.includes(source.value.connector)) return false;
    // const cm = sourceLatestMetrics.value.connector || {};
    // if (cm.snapshotState !== 'running' && cm.snapshotState !== 'pending') return true;

    // if (cm.connector_status?.toLowerCase() === 'paused') return false;
    // // allow snapshot only when all tables have completed previous snapshot
    // return false;
});

const canTopicCancelSnapshot = (topic) => {
    // Check if snapshot is disabled via global config
    if (!source.value) return false; // Wait for source to be loaded
    const snapshotDisabled = getPersistedGlobalConfigValue(source.value, 'incremental.snapshot.disabled');
    if (snapshotDisabled === true) return false;

    // if (NO_SNAPSHOT_CONNECTORS.includes(source.value.connector)) return false;
    switch (topic.snapshotStatus) {
        case 'pending':
        case 'running':
            return true;
        default:
            return false;
    }
};

const canTopicSnapshot = (topic) => {
    // Check if snapshot is disabled via global config
    if (!source.value) return false; // Wait for source to be loaded
    const snapshotDisabled = getPersistedGlobalConfigValue(source.value, 'incremental.snapshot.disabled');
    if (snapshotDisabled === true) return false;

    // if (NO_SNAPSHOT_CONNECTORS.includes(source.value.connector)) return false;
    // if (source.value.connector_status === 'Paused') return false;

    switch (topic.snapshotStatus) {
        case 'pending':
        case 'running':
            return false;
        default:
            return true;
    }
};

const checkTopic = (id, name, checked) => {
    checkedTopics.value[id] = checked;
    historicalCheckedTopicNames.value[id] = name;

    const allCheckedIds = Object.keys(checkedTopics.value).filter((x) => checkedTopics.value[x]);
    const currentTopics = topics(specificTopicMetricsToUse.value) || [];
    const allTopicsShownAreChecked = currentTopics.length > 0 && currentTopics.map((x) => x.id).every((x) => allCheckedIds.includes(x));

    allTopicsAreChecked.value = allTopicsShownAreChecked;
};

const scrollToCurrentPosition = async () => {
    const container = document.querySelector('.top-page-anchor');
    const scrollValue = _.cloneDeep(container?.scrollTop);

    await nextTick(() => {
        const latestContainer = document.querySelector('.top-page-anchor');
        const latestScrollValue = _.cloneDeep(latestContainer?.scrollTop);

        if (latestScrollValue !== scrollValue) {
            setTimeout(() => (container.scrollTop = scrollValue), 1); // Reset to captured position'
        }
    });
};

const anyTopicActionLoading = computed(() => {
    return Object.values(topicActionLoading.value).some((x) => x);
});

const checkAllTopics = (checked) => {
    const currentTopics = topics(specificTopicMetricsToUse.value) || [];
    currentTopics.forEach((topic) => {
        checkTopic(topic.id, topic.name, checked);
    });
};

// Simplified snapshot handler - shows options dialog for selected topics, direct confirmation for all topics
async function onSnapshot(specificTopics = [], topicIds = [], showOptions = false) {
    // Check if filtered snapshot is disabled - if so, skip options dialog
    if (showOptions && (specificTopics.length || topicIds.length) && !filteredSnapshotDisabled.value) {
        openSnapshotOptionsDialog(specificTopics, topicIds);
        return;
    }

    // For direct snapshots (all topics or single topic), show confirmation
    let actionName = 'trigger a snapshot on all tables for';
    let name = source.value.name;

    if (specificTopics?.length || topicIds?.length) {
        actionName = 'trigger a snapshot on';
        const topicNames = specificTopics.length ? specificTopics : topicIds;
        name = `${topicNames.length} topic${topicNames.length > 1 ? 's' : ''}`;
    }

    try {
        await useDeleteConfirmation({
            type: 'snapshot',
            title: 'Trigger Snapshot',
            message: `Are you sure you want to ${actionName} <strong>"${name}"</strong>?`,
            confirmText: 'Trigger Snapshot',
            requiresInput: true,
            inputPlaceholder: 'Type "snapshot" to confirm',
            expectedInput: 'snapshot'
        });

        await executeSnapshot(normalizeTopics(specificTopics, topicIds), []);
    } catch (error) {
        if (error.message) {
            console.error('Snapshot failed:', error);
        }
    }
}

// Utility function to normalize topics
function normalizeTopics(specificTopics, topicIds) {
    if (specificTopics.length) {
        return specificTopics;
    }
    return topicIds.map((id) => {
        const historicalTopic = historicalCheckedTopicNames.value[id];
        const currentTopic = topics(specificTopicMetricsToUse.value)?.find((x) => x.id === id);
        return historicalTopic || currentTopic || { id, name: id };
    });
}

function openSnapshotOptionsDialog(specificTopics, topicIds) {
    selectedTopicsForFiltering.value = normalizeTopics(specificTopics, topicIds);
    showSnapshotOptionsDialog.value = true;
}

// Handle snapshot request with proper filtering logic
async function handleSnapshotRequest(specificTopics = [], topicIds = []) {
    // If filtered snapshot is disabled, go directly to full snapshot
    if (filteredSnapshotDisabled.value) {
        await onSnapshot(specificTopics, topicIds, false);
    } else {
        // If enabled, show options dialog for filtered vs regular snapshot
        openSnapshotOptionsDialog(specificTopics, topicIds);
    }
}

async function executeSnapshot(specificTopics = [], topicIds = [], snapshotBody = null) {
    if (specificTopics.length) {
        specificTopics.forEach((topic) => {
            topicActionLoading.value[topic.id || topic] = true;
        });
    } else {
        actionLoading.value = true;
    }

    if (topicIds.length && !specificTopics.length) {
        specificTopics = topicIds.map(
            (id) => historicalCheckedTopicNames.value[id] || topics(specificTopicMetricsToUse.value)?.find((x) => x.id === id)
        );
    }

    try {
        const finalSnapshotBody = snapshotBody || {
            source_id: source.value.id,
            connector: source.value.connector,
            topic_names: specificTopics.length ? specificTopics.map((topic) => topic?.name || topic) : source.value?.topics
        };

        await sourcesStore.snapshot(finalSnapshotBody);

        // Show success message
        const hasFilters = snapshotBody?.additional_conditions?.length > 0;
        toast.success(hasFilters ? 'Filtered snapshot triggered successfully' : 'Snapshot triggered successfully');

        // after snapshot completes, schedule a delayed metrics refresh
        setTimeout(async () => {
            await metrics.sources.fetch(props.id, {
                topic_list_from: page.value * pageSize.value,
                topic_list_size: pageSize.value,
                time_type: 'latest'
            });
        }, 2000);
    } catch (error) {
        console.error('Snapshot execution failed:', error);
    } finally {
        // Clear loading states after API call completes
        if (specificTopics.length) {
            specificTopics.forEach((topic) => {
                topicActionLoading.value[topic.id || topic] = false;
                checkedTopics.value[topic.id || topic] = false;
            });
        } else {
            actionLoading.value = false;
        }
    }
}

// Handle filtered snapshot execution from the modal
const onFilteredSnapshotExecute = async (snapshotBody) => {
    try {
        await executeSnapshot(selectedTopicsForFiltering.value, [], snapshotBody);
        // Close dialog on successful execution
        showFilteredSnapshotDialog.value = false;
    } catch (error) {
        console.error('Filtered snapshot failed:', error);
    }
};

// Handle back navigation from filtered snapshot dialog
const onFilteredSnapshotBack = () => {
    // Show the snapshot options dialog again
    showSnapshotOptionsDialog.value = true;
};

// Handle snapshot options dialog events
const onSnapshotOptionsConfigureFilters = () => {
    showSnapshotOptionsDialog.value = false;
    // selectedTopicsForFiltering.value = selectedTopicsForFiltering.value; // Already normalized
    // Add a small delay to allow the first dialog's cleanup to complete before opening the second
    nextTick(() => {
        showFilteredSnapshotDialog.value = true;
    });
};

const onSnapshotOptionsRegularSnapshot = async () => {
    showSnapshotOptionsDialog.value = false;
    const topics = selectedTopicsForFiltering.value;
    await onSnapshot(topics, [], false); // Use onSnapshot to show confirmation dialog
};

const onSnapshotOptionsCancel = () => {
    showSnapshotOptionsDialog.value = false;
};

async function onCancelSnapshot(specificTopics = []) {
    if (specificTopics.length) {
        specificTopics.forEach((topic) => {
            topicActionLoading.value[topic.id || topic] = true;
        });
    } else {
        actionLoading.value = true;
    }

    try {
        const snapshotBody = {
            source_id: source.value.id,
            connector: source.value.connector,
            topic_names: specificTopics.length
                ? specificTopics.map((topic) => topic.name || topic)
                : topics(specificTopicMetricsToUse).value?.map((topic) => topic.name || topic)
        };
        await sourcesStore.cancelSnapshot(snapshotBody);
    } finally {
        if (specificTopics.length) {
            specificTopics.forEach((topic) => {
                topicActionLoading.value[topic.id || topic] = false;
            });
        } else {
            actionLoading.value = false;
        }
        // after cancel snapshot, schedule a delayed metrics refresh

        setTimeout(async () => {
            await metrics.sources.fetch(props.id, {
                topic_list_from: page.value * pageSize.value,
                topic_list_size: pageSize.value,
                time_type: 'latest'
            });
        }, 2000);
    }
}

const stats = computed(() => {
    const latencyMetric = getFormattedValueForLatest('latency', 'connector');
    const eventsWrittenMetric = getFormattedValueForLatest('sourceRecordWriteTotal', 'connector');
    const state = getFormattedValueForLatest('state', 'connector');

    return [
        {
            id: 0,
            name: 'Status',
            stat: (() => {
                const stateValue = state.display || sourceLatestMetrics.value?.inline?.state;
                const capitalizedStateValue = stateValue ? _.capitalize(stateValue) : '';
                const latencyIcon =
                    (latencyMetric.raw || sourceLatestMetrics.value?.inline?.latency) >= 60000
                        ? `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="h-5 w-5 text-destructive ml-2" title="High Latency">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                       </svg>`
                        : '';

                // Use responsive flexbox with wrap capability
                if (capitalizedStateValue) {
                    return `<div class="flex items-center flex-wrap gap-2 text-2xl font-bold">
                        <div class="flex items-center">
                            ${_.capitalize(sourceStatus.value)}
                            ${latencyIcon}
                        </div>
                        <span class="text-xs font-normal text-muted-foreground bg-muted px-1.5 py-0.5 rounded-md" title="${capitalizedStateValue}">
                            ${capitalizedStateValue}
                        </span>
                    </div>`;
                } else {
                    return `<div class="flex items-center text-2xl font-bold">
                        ${_.capitalize(sourceStatus.value)}
                        ${latencyIcon}
                    </div>`;
                }
            })(),
            icon: CheckCircle,
            infoIcon: sourceStatus.value === 'Broken',
            errors: sourceErrors.value,
            colorClass: getStatusColor(sourceStatus.value, false, true)
        },
        {
            id: 5,
            name: 'Snapshot',
            stat: sourceLatestMetrics.value.connector?.snapshotState
                ? _.capitalize(sourceLatestMetrics.value.connector?.snapshotState)
                : 'Not Started',
            valueTooltip: (() => {
                const cm = sourceLatestMetrics.value.connector || {};

                const total = Number(cm.SnapshotTotalTableCount) || 0;
                const remaining = Number(cm.SnapshotRemainingTableCount) || 0;
                const pct = total > 0 ? Math.round(((total - remaining) / total) * 100) : 0;
                const endTime = cm.snapshotEndTime;
                const startTime = cm.snapshotStartTime;

                if (!startTime && !endTime && !total) {
                    return '';
                }

                return total > 0
                    ? `Progress: <span class="text-xs">${remaining}/${total} tables remaining (${pct}%) <br> <br>Start: ${startTime} <br>End: ${endTime}</span>`
                    : `<span class="text-xs">Start: ${startTime} <br>End: ${endTime}</span>`;
            })(),
            icon: Video,
            colorClass: 'bg-chart-4' // Chart-4 for Snapshot
        },
        {
            id: 1,
            name: latencyMetric.metricDisplayName || 'Latency',
            stat: latencyMetric.display,
            icon: Clock,
            colorClass: 'bg-chart-3' // Orange for Latency
        },
        // {
        //     id: 2,
        //     name: 'Topics',
        //     stat: getTopicsLength(),
        //     icon: QueueListIcon,
        //     colorClass: 'bg-primary' // Blue for Topics
        // },
        {
            id: 3,
            name: eventsWrittenMetric.metricDisplayName || 'Events',
            stat: eventsWrittenMetric.display,
            icon: ChevronsLeft,
            colorClass: 'bg-primary' // Blue for Events Received
        },
        {
            id: 4,
            name: 'Errors',
            stat: getErrors(),
            icon: AlertCircle,
            colorClass: 'bg-destructive' // Red for Errors
        }
    ];
});

const getFormattedValueForLatest = (metricKey, level = 'inline', children = null, dashOnRule = null, otherMetricSource = null) => {
    const isChildren = children || _.isNumber(children);

    if (!source.value) return { display: '-' };

    const metricSource = otherMetricSource || sourceLatestMetrics.value;
    const metricValue = isChildren ? metricSource[level]?.[children]?.[metricKey] : metricSource[level]?.[metricKey];

    if (!connectorInfo.value) return { display: '-' };

    const metricFormattingInfo = getMetricFormattingInfo(metricKey, connectorInfo.value?.metrics);
    let formatterParams = [metricValue, metricFormattingInfo || {}];

    if (dashOnRule || dashOnRule === false) {
        formatterParams.push(dashOnRule);
    }

    return {
        ...(formatter(...formatterParams) || {}),
        metricDisplayName: metricFormattingInfo?.name
    };
};

const getErrors = () => {
    const errSum = sourceLatestMetrics?.value.topics
        ? topics(specificTopicMetricsToUse.value)?.value?.reduce((total, topic) => {
              const output = getFormattedValueForLatest(
                  'recordErrorTotal',
                  'topics',
                  topic.id,
                  null,
                  specificTopicMetricsToUse.value
              ).rawValueInCurrentUnit;
              return total + (output === '-' ? 0 : output);
          }, 0)
        : '0';

    return errSum > 0 ? errSum : '0';
};

let tabs = ref([
    { name: 'Status', href: '#', current: true },
    { name: 'Schema', href: '#', current: false },
    { name: 'Auth', href: '#', current: false },
    { name: 'Settings', href: '#', current: false },
    {
        name: 'Logs',
        href: `/logs?source_id=${props.id}`,
        target: '_blank',
        rel: 'noopener noreferrer',
        newTabUrl: true,
        current: false
    }
]);

// currentTab now managed by useUrlFilters composable above

const setCurrentTab = (str) => {
    if (str === 'Logs') {
        return;
    }

    currentTab.value = str;
    // URL updated automatically by useUrlFilters composable
};

const sourceErrors = computed(() => {
    if (sourceStatus.value === 'Broken' && source.value?.task_statuses) {
        return [...new Set(Object.values(source.value?.task_statuses).map((task) => task.trace))];
    }

    return [];
});

const sourceStatus = computed(() => {
    return (
        sourceLatestMetrics?.value?.inline?.latest?.connector_status ??
        source?.value?.connector_status ??
        (source?.value?.connector === 'kafkadirect' ? 'Active' : 'Unknown')
    );
});

async function saveSource() {
    tabLoading.value = true;
    formError.value = '';
    editTreeLists(treeNodes.value);

    // Inject default config values for unchanged properties
    if (sourceConfig.value) {
        source.value.config = source.value.config || {};
        sourceConfig.value.forEach((control) => {
            const name = control.name;
            const defaultVal = control.value?.default;
            if (defaultVal !== undefined && source.value.config[name] === undefined) {
                source.value.config[name] = defaultVal;
            }
        });
    }

    try {
        await sourcesStore.edit(source.value, source.value.id);
        await loadSourceUponAction();

        canSave.value = false;
        canSaveSchemaTree.value = false;

        toast.success('Source successfully saved.');
    } catch (err) {
        console.error('Error in saveSource:', err);
        formError.value = err;
        tabLoading.value = false;
    } finally {
        tabLoading.value = false;
    }
}

function onDiscard() {
    source.value = _.cloneDeep(defaultSource.value);
    constructTreeNodes();
    canSave.value = false;
    canSaveSchemaTree.value = false;
}

async function handleTimeseriesDateFilterInput(dateFilter, specificTimeUnit) {
    selectedTimeseriesDateFilter.value = dateFilter;
    selectedTimeseriesTimeUnit.value = specificTimeUnit;
    // Data fetching is now handled automatically by watchers
    await scrollToCurrentPosition();
}

function onTimeseriesTimeRangeChange(timeRangeValue) {
    selectedTimeseriesTimeRange.value = timeRangeValue;

    // Use utility function to create proper date filter with correct timestamps
    const dateFilter = createDateFilterFromTimeRange(timeRangeValue);
    selectedTimeseriesTimeUnit.value = dateFilter.timeUnit;
    handleTimeseriesDateFilterInput(dateFilter, dateFilter.timeUnit);
}

function onTopicsTimeRangeChange(timeRangeValue) {
    selectedTopicsTimeRange.value = timeRangeValue;

    // Use same utility function as timeseries for consistency
    const dateFilter = createDateFilterFromTimeRange(timeRangeValue);

    handleTopicsDateFilterInput(dateFilter);
}

async function handleTopicsDateFilterInput(dateFilter) {
    selectedTopicsDateFilter.value = dateFilter;
    // Data fetching is now handled automatically by watchers
}

function constructTreeLists(nodesList) {
    const outputMap = {};

    treeLevels.value.forEach((level) => {
        outputMap[level.schemaObject] = [];
    });

    const processNode = (node, parentName) => {
        if (node.isSelected) {
            const schemaObject = node.schemaObject;
            if (schemaObject) {
                const nodeName = parentName ? `${parentName}.${node.name}` : node.name;
                outputMap[schemaObject].push(nodeName);
            }
            if (node.children) {
                node.children.forEach((childNode) => processNode(childNode, node.name));
            }
        }
    };

    nodesList.forEach((node) => processNode(node, null));
    return outputMap;
}

function editTreeLists(nodesList) {
    const outputMap = constructTreeLists(nodesList);

    for (const schemaObject in outputMap) {
        if (source.value.config) {
            source.value.config[schemaObject] = outputMap[schemaObject].join(', ');
        }
    }
}

function onFieldChanged({ value, valid, name }) {
    const control = sourceConfig.value.find((x) => x.name == name);

    if (control) {
        control.value.current_value = value;
        control.value.valid = valid;

        sourceConfig.value = _.chain(adjustControlConditionalValues(sourceConfig.value))
            .map((control) => adjustControlConditionalVisibility(control, sourceConfig.value)?.control || null)
            .compact()
            .value();
    }

    if (valid && !_.isUndefined(value)) {
        if (source.value) {
            source.value.config[name] = value;
        }
    }
}

const chartOptions = computed(() => {
    const titleText = getTimeRangeLabel(selectedTimeseriesDateFilter.value?.key) || TIME_RANGE_LABELS.LAST_24H;

    const metricKeys = _.cloneDeep(selectedTimeseriesMetrics.value)?.map((x) => x.key) || [];

    const dataForEachSelectedMetric = metricKeys.map((metricKey) => {
        const metricData =
            _.isNil(plotSpecificMetrics.value) || _.isEmpty(plotSpecificMetrics.value)
                ? sourceTimeseriesConnectorMetrics.value[metricKey]
                : plotSpecificMetrics.value?.connector[metricKey];

        return {
            metricKey,
            metricData,
            metricInfo: getMetricFormattingInfo(metricKey, connectorInfo.value?.metrics)
        };
    });

    const seriesAndYAxes = dataForEachSelectedMetric.map((metric, index) => {
        const yAxis = {
            title: {
                text: metric.metricInfo?.name
            },
            labels: {
                formatter: function () {
                    return formatter(this.value, { unit: metric.metricInfo?.unit }, false).display;
                }
            },
            opposite: index % 2 !== 0
        };

        const series = {
            name: metric.metricInfo?.name,
            data: metric.metricData?.map((dataItem) => {
                const localTime = moment.utc(dataItem.timestamp).tz(moment.tz.guess()).toDate();
                return {
                    x: localTime,
                    y: dataItem.value
                };
            }),
            type: 'line',
            yAxis: index,
            custom: {
                unit: metric.metricInfo?.unit
            },
            dataLabels: {
                enabled: true,
                formatter: function () {
                    return formatter(this.y, { unit: metric.metricInfo?.unit }, false).display;
                }
            }
        };

        return { series, yAxis };
    });

    // Split the series and yAxis configurations
    const series = seriesAndYAxes.map((sy) => sy.series);
    const yAxis = seriesAndYAxes.map((sy) => sy.yAxis);

    const options = {
        time: {
            timezone: moment.tz.guess()
        },
        chart: {
            zoomType: 'xy'
        },
        title: {
            text: titleText
        },
        subtitle: {
            text:
                selectedTimeseriesTimeUnit.value === FILTER_DEFAULTS.TIME_UNITS.HOUR
                    ? 'Hourly Data'
                    : selectedTimeseriesTimeUnit.value === 'minute'
                      ? 'Minute Data'
                      : selectedTimeseriesTimeUnit.value === 'month'
                        ? 'Monthly Data'
                        : 'Daily Data'
        },
        caption: {
            text: `Timezone: ${moment.tz.guess()}`
        },
        xAxis: {
            type: 'datetime',
            labels: {
                formatter: formatChartXAxisLabels
            }
        },
        yAxis: yAxis,
        series: series,
        tooltip: {
            shared: true,
            crosshairs: true,
            formatter: function () {
                const localTime = moment(new Date(this.x)).tz(moment.tz.guess()).format('YYYY-MM-DD HH:mm:ss');

                let tooltip = `${localTime}<br>`;
                this.points.forEach((point) => {
                    tooltip += `<b>${point.series.name}:</b> ${
                        formatter(point.y, { unit: point.series.userOptions.custom?.unit }, false).display
                    }<br>`;
                });
                return tooltip;
            }
        }
    };

    return options;
});

const goToTopic = (topic) => {
    router.push({ name: 'topic-details', params: { topic_id: topic.id } });
};

function clearAllFilters() {
    resetAllFilters();
    // Will automatically trigger data refetch due to watchers
}

async function onCopyId(id) {
    try {
        await navigator.clipboard.writeText(id);
        toast.success('ID copied to clipboard');
    } catch (error) {
        console.error('Failed to copy ID to clipboard:', error);
    }
}
</script>

<template>
    <div>
        <div v-if="source">
            <PageBreadcrumb>
                <template #actions>
                    <ActionMenu
                        variant="menu"
                        menu-trigger-size="md"
                        :show-delete="userHasAccess([$Permissions.DELETE_SOURCES], authStore.user)"
                        :show-reset="userHasAccess([$Permissions.TRIGGER_ACTION_SOURCES], authStore.user)"
                        :show-clone="userHasAccess([$Permissions.WRITE_SOURCES], authStore.user)"
                        :show-stop="
                            source?.connector !== 'kafkadirect' &&
                            userHasAccess([$Permissions.TRIGGER_ACTION_SOURCES], authStore.user) &&
                            sourceStatus !== 'Paused' &&
                            sourceStatus !== 'Stopped'
                        "
                        :show-restart="
                            userHasAccess([$Permissions.TRIGGER_ACTION_SOURCES], authStore.user) &&
                            sourceStatus !== 'Active' &&
                            sourceStatus !== 'Paused' &&
                            sourceStatus !== 'Stopped'
                        "
                        :show-resume="
                            source?.connector !== 'kafkadirect' &&
                            userHasAccess([$Permissions.TRIGGER_ACTION_SOURCES], authStore.user) &&
                            sourceStatus !== 'Active' &&
                            sourceStatus !== 'Broken'
                        "
                        :show-snapshot="userHasAccess([$Permissions.TRIGGER_ACTION_SOURCES], authStore.user) && canConnectorSnapshot"
                        :show-cancel-snapshot="userHasAccess([$Permissions.TRIGGER_ACTION_SOURCES], authStore.user) && canConnectorCancelSnapshot"
                        :show-copy-id="true"
                        :loading="actionLoading || tabLoading"
                        @delete="onDelete('source', source.name, source.id)"
                        @stop="onStop(source.id)"
                        @resume="onResume(source.id)"
                        @restart="onRestart(source.id)"
                        @reset="onReset(source)"
                        @snapshot="onSnapshot()"
                        @clone="onClone(source)"
                        @cancel-snapshot="onCancelSnapshot()"
                        @copyId="onCopyId(source.id)" />
                </template>
            </PageBreadcrumb>
        </div>
        <div>
            <AppLoader :listen="['sources.fetch']" :force-loading="generalLoading" default-height="60vh" class="min-h-60">
                <div v-if="source" class="w-full top-page-anchor">
                    <Tabs :tabs="tabs" :initial-active-tab="currentTab" :loading="tabLoading" @tab-change="setCurrentTab">
                        <template #Status>
                            <div class="mx-auto w-full mt-5">
                                <div class="mb-5">
                                    <div class="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-5">
                                        <StatCard
                                            v-for="item in stats"
                                            :key="item.id"
                                            :no-of-stats="stats.length"
                                            :icon-bg-class="item.colorClass"
                                            :valueTooltip="item.valueTooltip"
                                            :tooltipAsIcon="true"
                                            :icon="item.icon"
                                            :info-icon="item.infoIcon"
                                            :errors="item.errors"
                                            :loading="metricsLoading || configurationLoading"
                                            :title="item.name"
                                            :value="item.stat" />
                                    </div>
                                </div>

                                <Card class="bg-sidebar-primary-foreground">
                                    <CardHeader>
                                        <div class="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                                            <div class="flex flex-col gap-3 sm:flex-row sm:items-end sm:gap-4">
                                                <TimeRangeFilter
                                                    label="Time Range"
                                                    :model-value="convertFilterKeyToTimeRangeValue(selectedTimeseriesDateFilter?.key || 'last24h')"
                                                    :disabled="timeseriesLoading || metricsLoading"
                                                    placeholder="Select time range"
                                                    :include-live-option="false"
                                                    :include-all-time-option="false"
                                                    :include-date-options="true"
                                                    :include-short-ranges="false"
                                                    :hide-options="[
                                                        'today',
                                                        'yesterday',
                                                        FILTER_DEFAULTS.DATE_FILTERS.ALL_TIME,
                                                        'lastWeek',
                                                        'lastMonth',
                                                        'thisWeek',
                                                        'thisMonth'
                                                    ]"
                                                    @update:model-value="onTimeseriesTimeRangeChange" />
                                                <SelectDropdown
                                                    label="Time Grain"
                                                    :options="timeUnitOptions"
                                                    :model-value="selectedTimeseriesTimeUnit"
                                                    :disabled="timeseriesLoading"
                                                    @update:model-value="
                                                        (value) => handleTimeseriesDateFilterInput(selectedTimeseriesDateFilter, value)
                                                    " />
                                            </div>
                                            <div class="flex flex-col space-y-1 min-w-0 sm:max-w-xs">
                                                <div class="text-xs text-muted-foreground">Metrics</div>
                                                <MultiSelect
                                                    v-model="selectedMetricKeys"
                                                    :options="listOfTimeseriesMetricOptions"
                                                    :placeholder="
                                                        timeseriesLoading
                                                            ? 'Loading...'
                                                            : listOfTimeseriesMetricOptions?.length
                                                              ? 'Select metrics'
                                                              : 'No metrics available'
                                                    "
                                                    :disabled="timeseriesLoading || !listOfTimeseriesMetricOptions?.length"
                                                    :option-label="'name'"
                                                    :track-by="'key'"
                                                    :show-tags="false"
                                                    :enable-refresh="true"
                                                    @refresh="refreshTimeseries()" />
                                            </div>
                                        </div>
                                    </CardHeader>
                                    <CardContent class="pt-0">
                                        <HighChart ref="chart" :chart-options="chartOptions" :loading="timeseriesLoading || metricsLoading" />
                                    </CardContent>
                                </Card>
                                <div class="mt-5">
                                    <div>
                                        <div class="w-full flex justify-between items-center mb-3">
                                            <h4 class="font-medium">Topics</h4>
                                            <div class="flex items-center gap-3">
                                                <TimeRangeFilter
                                                    v-model="selectedTopicsTimeRange"
                                                    :loading="topicsLoading"
                                                    placeholder="Select time range"
                                                    :include-live-option="false"
                                                    :include-all-time-option="true"
                                                    :include-date-options="true"
                                                    :include-short-ranges="false"
                                                    :hide-options="['today', 'yesterday']"
                                                    @update:model-value="onTopicsTimeRangeChange" />
                                            </div>
                                        </div>

                                        <section>
                                            <div>
                                                <!-- Topics Table Component -->
                                                <ActionMenu
                                                    v-if="
                                                        userHasAccess([$Permissions.TRIGGER_ACTION_SOURCES], authStore.user) &&
                                                        (canConnectorSnapshot || canConnectorCancelSnapshot)
                                                    "
                                                    variant="row"
                                                    :left="true"
                                                    class="mb-2"
                                                    :show-snapshot="canConnectorSnapshot"
                                                    :show-cancel-snapshot="canConnectorCancelSnapshot"
                                                    :disable-snapshot="Object.values(checkedTopics).every((x) => !x)"
                                                    :disable-cancel-snapshot="Object.values(checkedTopics).every((x) => !x)"
                                                    :loading="topicsLoading || anyTopicActionLoading || actionLoading"
                                                    @snapshot="
                                                        handleSnapshotRequest(
                                                            [],
                                                            Object.keys(checkedTopics).filter((x) => checkedTopics[x])
                                                        )
                                                    "
                                                    @cancel-snapshot="onCancelSnapshot()" />

                                                <div class="w-full border shadow-md rounded-md h-auto">
                                                    <ScrollArea
                                                        :class="(topics(specificTopicMetricsToUse)?.length || 0) <= 10 ? 'h-auto' : 'h-70-dynamic'"
                                                        class="w-full">
                                                        <Table>
                                                            <TableHeader class="sticky top-0 bg-background z-10">
                                                                <TableRow class="bg-muted/50">
                                                                    <TableHead class="first:rounded-tl-md">
                                                                        <div class="flex gap-2 items-center">
                                                                            <Checkbox
                                                                                v-if="
                                                                                    userHasAccess(
                                                                                        [$Permissions.TRIGGER_ACTION_SOURCES],
                                                                                        authStore.user
                                                                                    ) && canConnectorSnapshot
                                                                                "
                                                                                :checked="allTopicsAreChecked"
                                                                                @update:modelValue="checkAllTopics" />
                                                                            Topic
                                                                        </div>
                                                                    </TableHead>
                                                                    <!-- Dynamic metric headers with skeleton loading -->
                                                                    <template v-if="configurationLoading || !availableMetrics?.topic?.length">
                                                                        <TableHead v-for="n in 3" :key="`skeleton-header-${n}`">
                                                                            <SkeletonLoader preset="header-small" :loading="true" />
                                                                        </TableHead>
                                                                    </template>
                                                                    <template v-else>
                                                                        <TableHead v-for="metric in availableMetrics?.topic" :key="metric.attribute">
                                                                            {{ metric.display.name }}
                                                                        </TableHead>
                                                                    </template>
                                                                    <TableHead> Snapshot </TableHead>
                                                                    <TableHead class="last:rounded-tr-md" />
                                                                </TableRow>
                                                            </TableHeader>
                                                            <TableBody>
                                                                <!-- Skeleton rows when loading -->
                                                                <template v-if="topicsLoading || metricsLoading">
                                                                    <TableRow v-for="n in 5" :key="`skeleton-${n}`">
                                                                        <TableCell><SkeletonLoader preset="table-name" :loading="true" /></TableCell>
                                                                        <TableCell
                                                                            v-for="metric in availableMetrics?.topic || [
                                                                                { attribute: 'metric1' },
                                                                                { attribute: 'metric2' },
                                                                                { attribute: 'metric3' }
                                                                            ]"
                                                                            :key="metric.attribute">
                                                                            <SkeletonLoader preset="table-metric" :loading="true" />
                                                                        </TableCell>
                                                                        <TableCell
                                                                            ><SkeletonLoader preset="table-status" :loading="true"
                                                                        /></TableCell>
                                                                        <TableCell
                                                                            ><SkeletonLoader preset="table-action" :loading="true"
                                                                        /></TableCell>
                                                                    </TableRow>
                                                                </template>
                                                                <!-- Actual data rows -->
                                                                <template v-else>
                                                                    <TableRow
                                                                        v-for="(topic, index) in topics(specificTopicMetricsToUse)"
                                                                        :key="index">
                                                                        <TableCell>
                                                                            <div class="flex items-center gap-3">
                                                                                <Checkbox
                                                                                    v-if="canConnectorSnapshot"
                                                                                    :checked="!!checkedTopics[topic.id]"
                                                                                    @update:modelValue="
                                                                                        (checked) =>
                                                                                            checkTopic(topic.id, topic.name || topic, checked)
                                                                                    " />
                                                                                <Button
                                                                                    variant="link"
                                                                                    class="h-auto p-0 max-w-full text-left justify-start font-medium"
                                                                                    :title="topic.name"
                                                                                    @click="goToTopic(topic)">
                                                                                    <span
                                                                                        class="truncate max-w-[200px] sm:max-w-[250px] md:max-w-[300px] lg:max-w-[350px] xl:max-w-[400px]">
                                                                                        {{ topic.name }}
                                                                                    </span>
                                                                                </Button>
                                                                            </div>
                                                                        </TableCell>
                                                                        <TableCell v-for="metric in availableMetrics?.topic" :key="metric.attribute">
                                                                            <span>
                                                                                {{
                                                                                    getFormattedValueForLatest(
                                                                                        metric.attribute,
                                                                                        'topics',
                                                                                        topic.id,
                                                                                        null,
                                                                                        specificTopicMetricsToUse
                                                                                    ).display
                                                                                }}
                                                                                <span
                                                                                    v-if="
                                                                                        metric.attribute === 'StreamingLastEvent' &&
                                                                                        !getFormattedValueForLatest(
                                                                                            'StreamingMilliSecondsSinceLastEvent',
                                                                                            'topics',
                                                                                            topic.id,
                                                                                            null,
                                                                                            specificTopicMetricsToUse
                                                                                        ).display === '-'
                                                                                    ">
                                                                                    (
                                                                                    {{
                                                                                        getFormattedValueForLatest(
                                                                                            'StreamingMilliSecondsSinceLastEvent',
                                                                                            'topics',
                                                                                            topic.id,
                                                                                            null,
                                                                                            specificTopicMetricsToUse
                                                                                        ).display
                                                                                    }}
                                                                                    ago)
                                                                                </span>
                                                                            </span>
                                                                        </TableCell>
                                                                        <TableCell>
                                                                            <div class="flex flex-col gap-1">
                                                                                <div>
                                                                                    <Badge
                                                                                        :variant="
                                                                                            getSnapshotStatusStyling(topic.snapshotStatus).variant
                                                                                        "
                                                                                        :class="getSnapshotStatusStyling(topic.snapshotStatus).class">
                                                                                        {{ topic.snapshotDisplayStatus }}
                                                                                    </Badge>
                                                                                </div>
                                                                                <div
                                                                                    v-if="topic.snapshotStatus && topic.rawStatus"
                                                                                    class="text-xs text-muted-foreground">
                                                                                    <div>
                                                                                        Start:
                                                                                        {{
                                                                                            topic.snapshotStartTimestamp
                                                                                                ? moment(topic.snapshotStartTimestamp).format(
                                                                                                      'YYYY-MM-DD HH:mm:ss'
                                                                                                  )
                                                                                                : '-'
                                                                                        }}
                                                                                    </div>
                                                                                    <div>
                                                                                        End:
                                                                                        {{
                                                                                            topic.snapshotEndTimestamp
                                                                                                ? moment(topic.snapshotEndTimestamp).format(
                                                                                                      'YYYY-MM-DD HH:mm:ss'
                                                                                                  )
                                                                                                : '-'
                                                                                        }}
                                                                                    </div>
                                                                                    <div>
                                                                                        Duration:
                                                                                        {{
                                                                                            topic.snapshotDurationMs != null
                                                                                                ? formatter(topic.snapshotDurationMs, {
                                                                                                      unit: 'milliseconds'
                                                                                                  }).display
                                                                                                : '-'
                                                                                        }}
                                                                                    </div>
                                                                                    <div>
                                                                                        Rows:
                                                                                        {{
                                                                                            topic.snapshotRowsScanned != null
                                                                                                ? formatter(topic.snapshotRowsScanned, {}).display
                                                                                                : '-'
                                                                                        }}
                                                                                        <span
                                                                                            v-if="
                                                                                                topic.snapshotRowsTotal &&
                                                                                                topic.snapshotRowsTotal !== '0'
                                                                                            ">
                                                                                            /

                                                                                            {{
                                                                                                topic.snapshotRowsTotal != null
                                                                                                    ? formatter(topic.snapshotRowsTotal, {}).display
                                                                                                    : '-'
                                                                                            }}
                                                                                            <span
                                                                                                v-if="topic.snapshotRowsTotal > 0"
                                                                                                class="ml-1 text-muted-foreground"
                                                                                                >(
                                                                                                {{
                                                                                                    Math.round(
                                                                                                        (topic.snapshotRowsScanned /
                                                                                                            topic.snapshotRowsTotal) *
                                                                                                            100
                                                                                                    )
                                                                                                }}% )</span
                                                                                            >
                                                                                        </span>
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        </TableCell>
                                                                        <TableCell>
                                                                            <ActionMenu
                                                                                :key="index"
                                                                                variant="menu"
                                                                                :loading="topicActionLoading[topic.id]"
                                                                                :show-cancel-snapshot="
                                                                                    userHasAccess(
                                                                                        [$Permissions.TRIGGER_ACTION_SOURCES],
                                                                                        authStore.user
                                                                                    ) && canTopicCancelSnapshot(topic)
                                                                                "
                                                                                :show-snapshot="
                                                                                    userHasAccess(
                                                                                        [$Permissions.TRIGGER_ACTION_SOURCES],
                                                                                        authStore.user
                                                                                    ) && canTopicSnapshot(topic)
                                                                                "
                                                                                @cancelSnapshot="onCancelSnapshot([topic], true)"
                                                                                @snapshot="handleSnapshotRequest([topic])" />
                                                                        </TableCell>
                                                                    </TableRow>
                                                                </template>
                                                                <TableEmpty
                                                                    v-if="
                                                                        !(
                                                                            topics(specificTopicMetricsToUse)?.length ||
                                                                            topicsLoading ||
                                                                            metricsLoading
                                                                        )
                                                                    "
                                                                    :colspan="
                                                                        4 +
                                                                        (availableMetrics?.topic?.length ||
                                                                            (configurationLoading || !availableMetrics?.topic?.length ? 3 : 0))
                                                                    ">
                                                                    <TableEmptyState />
                                                                </TableEmpty>
                                                            </TableBody>
                                                        </Table>
                                                        <ScrollBar orientation="horizontal" />
                                                        <ScrollBar orientation="vertical" />
                                                    </ScrollArea>
                                                </div>
                                            </div>
                                            <div v-if="!(metricsLoading || topicsLoading)" class="pt-2 pb-10">
                                                <div class="flex items-center justify-between gap-4 flex-wrap sm:flex-nowrap">
                                                    <div class="flex items-center gap-4 min-w-0">
                                                        <div class="text-sm text-muted-foreground whitespace-nowrap">
                                                            Showing topics {{ page * pageSize + 1 }} to
                                                            {{ Math.min((page + 1) * pageSize, source.topics?.length ?? 0) }} of
                                                            {{ source.topics?.length ?? 0 }}
                                                        </div>
                                                        <ItemsPerPageSelect
                                                            v-model="pageSize"
                                                            :loading="topicsLoading"
                                                            include-small-sizes
                                                            @update:model-value="onItemsPerPageChange" />
                                                    </div>
                                                    <div class="flex justify-end">
                                                        <Pagination
                                                            :page="page + 1"
                                                            :items-per-page="pageSize"
                                                            :total="source.topics?.length ?? 0"
                                                            :disabled="metricsLoading || topicsLoading"
                                                            :sibling-count="1"
                                                            :show-edges="true"
                                                            @update:page="onTopicsPageChange($event - 1)">
                                                            <PaginationContent>
                                                                <PaginationPrevious />
                                                                <template v-for="topicPage in getVisibleTopicsPages()" :key="topicPage">
                                                                    <PaginationItem
                                                                        v-if="topicPage !== '...'"
                                                                        :value="topicPage"
                                                                        :is-active="topicPage === page + 1"
                                                                        @click="onTopicsPageChange(topicPage - 1)">
                                                                        {{ topicPage }}
                                                                    </PaginationItem>
                                                                    <span v-else class="flex h-9 w-9 items-center justify-center">…</span>
                                                                </template>
                                                                <PaginationNext />
                                                            </PaginationContent>
                                                        </Pagination>
                                                    </div>
                                                </div>
                                            </div>
                                        </section>
                                    </div>
                                </div>
                            </div>
                        </template>
                        <template #Schema>
                            <Card v-if="noExistingSchema || !treeLevels?.[0]" class="mt-5">
                                <CardContent class="p-12">
                                    <div class="text-center w-full">
                                        <div class="flex flex-col w-full max-w-sm px-4 mx-auto items-center justify-center">
                                            <div class="p-4 mx-auto text-muted-foreground bg-muted/30 rounded-full">
                                                <Search class="h-8 w-8" />
                                            </div>
                                            <h3 class="mt-4 text-lg font-medium text-foreground">No schema available</h3>
                                            <p class="mt-2 text-sm text-muted-foreground max-w-sm">
                                                This connector does not provide schema configuration options.
                                            </p>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                            <template v-else>
                                <Card class="mt-5">
                                    <CardContent>
                                        <FormSkeleton v-if="treeLoading" :field-count="3" :show-advanced="false" />
                                        <template v-else>
                                            <div :class="visibleSchemaConnectorConfigControls?.length ? 'mt-10' : 'mt-3'">
                                                <div
                                                    v-show="control.display_in_ui"
                                                    v-for="(control, index) in visibleSchemaConnectorConfigControls"
                                                    :key="index">
                                                    <DynamicField
                                                        :key="`${control.name}_${control.display_in_ui}`"
                                                        :config="control"
                                                        :input-class="getClass(control, index)"
                                                        :hide-lateral-description="hideLateralDescriptionValue"
                                                        @input="onFieldChanged" />
                                                </div>
                                            </div>
                                            <Label v-if="treeNodes?.length || treeLevels.length" class="text-base mb-2">Schema</Label>
                                            <TreeView
                                                class="w-full overflow-y-auto mb-5 max-h-80-dynamic"
                                                :tree-nodes="treeNodes || []"
                                                :tree-levels="treeLevels || []"
                                                :loading="treeLoading"
                                                :allow-search="true"
                                                :edit-mode="true"
                                                :connector-type="source.connector"
                                                @can-save-duplicate="($event) => (schemaTreeIsValid = $event)" />
                                        </template>
                                    </CardContent>
                                </Card>
                            </template>
                        </template>
                        <template #smt>
                            <div class="mt-5">
                                <FormSkeleton v-if="configurationLoading || !sourceConfig" :field-count="4" />
                                <template v-else>
                                    <ConnectorTab
                                        v-if="source.config && sourceConfig"
                                        :key="source.config"
                                        :connector="source"
                                        :default-connector="defaultSource"
                                        :config="sourceConfig"
                                        :justify-content="'start'"
                                        :tab="'smt'"
                                        :entity-type="'source'"
                                        :edit-mode="true"
                                        @changed="(status) => updateCanSave(status)" />
                                    <ErrorMessage v-if="formError" :error="formError" :to-sentry="true" :context="'edit-source'" />
                                </template>
                            </div>
                        </template>
                        <template #Auth>
                            <div class="mt-5">
                                <FormSkeleton v-if="treeLoading || !sourceConfig" :field-count="3" />
                                <template v-else>
                                    <ConnectorTab
                                        v-if="source.config && sourceConfig"
                                        :key="source.config"
                                        :connector="source"
                                        :default-connector="defaultSource"
                                        :config="sourceConfig"
                                        :justify-content="'start'"
                                        :tab="'auth'"
                                        :entity-type="'source'"
                                        :edit-mode="true"
                                        @changed="(status) => updateCanSave(status)" />
                                    <ErrorMessage v-if="formError" :error="formError" :to-sentry="true" :context="'edit-source'" />
                                </template>
                            </div>
                        </template>
                        <template #Settings>
                            <div class="mt-5">
                                <FormSkeleton v-if="treeLoading || !sourceConfig" :field-count="6" />
                                <template v-else>
                                    <ConnectorTab
                                        v-if="source.config && sourceConfig"
                                        :key="source.config"
                                        :connector="source"
                                        :default-connector="defaultSource"
                                        :config="sourceConfig"
                                        :justify-content="'start'"
                                        :tab="'settings'"
                                        :entity-type="'source'"
                                        :edit-mode="true"
                                        @changed="(status) => updateCanSave(status)" />
                                    <ErrorMessage v-if="formError" :error="formError" :to-sentry="true" :context="'edit-source'" />
                                </template>
                            </div>
                        </template>
                    </Tabs>
                </div>
            </AppLoader>
        </div>

        <!-- Footer will teleport to App.vue footer slot -->
        <div v-if="source" v-can-access.permissions="[$Permissions.WRITE_SOURCES]">
            <AppFooter>
                <template #right>
                    <ActionMenu
                        variant="row"
                        :show-save="true"
                        :disable-save="!canSaveButton || tabLoading || !source.name || (currentTab === 'Schema' && !hasAnySchemaSelection)"
                        :loading="actionLoading || tabLoading"
                        @save="saveSource"
                        @discard="onDiscard" />
                </template>
            </AppFooter>
        </div>

        <!-- Snapshot Options Dialog -->
        <SnapshotOptionsDialog
            key="snapshot-options-dialog"
            v-show="showSnapshotOptionsDialog && !filteredSnapshotDisabled"
            v-model:open="showSnapshotOptionsDialog"
            :selected-topics="selectedTopicsForFiltering"
            @configure-filters="onSnapshotOptionsConfigureFilters"
            @regular-snapshot="onSnapshotOptionsRegularSnapshot"
            @cancel="onSnapshotOptionsCancel" />

        <!-- Filtered Snapshot Dialog -->
        <FilteredSnapshotDialog
            key="filtered-snapshot-dialog"
            v-show="showFilteredSnapshotDialog && !filteredSnapshotDisabled"
            v-model:open="showFilteredSnapshotDialog"
            :selected-topics="selectedTopicsForFiltering"
            :source-id="source?.id"
            :connector="source?.connector"
            :language="filteredSnapshotLanguage"
            @execute="onFilteredSnapshotExecute"
            @back="onFilteredSnapshotBack" />
    </div>
</template>
